import { keys } from './keys';
import { isArray } from '../array';
/**
 * Функция для глубокого (рекурсивного) сравнения двух неизвестных значений.
 *
 * @param objA Первое значение для сравнения.
 * @param objB Второе значение для сравнения.
 * @param maxDepth Максимальная глубина сравнения.
 * @returns true, если два переданных значения равны, иначе false.
 * @example
 * ```ts
 * // Простые объекты
 * const obj1 = { name: 'John', age: 30, hobbies: ['reading', 'swimming'] };
 * const obj2 = { name: 'John', age: 30, hobbies: ['reading', 'swimming'] };
 * const obj3 = { name: 'Jane', age: 25, hobbies: ['reading'] };
 *
 * console.log(deepEqual(obj1, obj2)); // true
 * console.log(deepEqual(obj1, obj3)); // false
 * ```
 * @example
 * ```ts
 * // Вложенные объекты
 * const obj1 = {
 *   user: {
 *     profile: {
 *       name: 'John',
 *       address: { city: 'Moscow', street: 'Lenina' }
 *     }
 *   }
 * };
 *
 * const obj2 = {
 *   user: {
 *     profile: {
 *       name: 'John',
 *       address: { city: 'Moscow', street: 'Lenina' }
 *     }
 *   }
 * };
 *
 * console.log(deepEqual(obj1, obj2)); // true
 * ```
 * @example
 * ```ts
 * // Ограничение глубины сравнения
 * const obj1 = { a: { b: { c: { d: 1 } } } };
 * const obj2 = { a: { b: { c: { d: 2 } } } };
 *
 * console.log(deepEqual(obj1, obj2, 2)); // true (сравнивается только до уровня b)
 * console.log(deepEqual(obj1, obj2, 3)); // false (сравнивается до уровня c)
 * ```
 * @example
 * ```ts
 * // Особые случаи: NaN, массивы, циклические ссылки
 * console.log(deepEqual(NaN, NaN)); // true
 * console.log(deepEqual([1, 2, 3], [1, 2, 3])); // true
 *
 * const objA: any = {};
 * objA.self = objA;
 * const objB: any = {};
 * objB.self = objB;
 * console.log(deepEqual(objA, objB)); // true (циклические ссылки обрабатываются корректно)
 * ```
 */
export function deepEqual(objA, objB, maxDepth = Infinity, cache = new WeakMap()) {
    if (is(objA, objB)) {
        return true;
    }
    if (typeof objA !== 'object' || objA === null || typeof objB !== 'object' || objB === null) {
        return false;
    }
    /**
     * В кэше для каждой посещенной ссылки на объект хранится то, с чем его сравнивали
     */
    const prevComparedObjB = cache.get(objA);
    /**
     * Если есть что-то в кэше, то objA уже был посещен, значит это объект(ы) с циклическими ссылками
     */
    if (prevComparedObjB) {
        /**
         * Если в прошлый раз он сравнивался с тем же самым, значит цикл одинаковый для обоих объектов
         */
        if (prevComparedObjB === objB) {
            return true;
        }
        return false;
    }
    cache.set(objA, objB);
    if (maxDepth === 0) {
        return false;
    }
    const normalizedObjA = isIterableObject(objA) ? iterableToObject(objA) : objA;
    const normalizedObjB = isIterableObject(objB) ? iterableToObject(objB) : objB;
    const keysA = keys(normalizedObjA);
    const keysB = keys(normalizedObjB);
    if (keysA.length !== keysB.length) {
        return false;
    }
    for (let i = 0; i < keysA.length; i++) {
        const keyA = keysA[i];
        if (!keyA) {
            return false;
        }
        if (!Object.prototype.hasOwnProperty.call(normalizedObjB, keyA) ||
            !deepEqual(normalizedObjA[keyA], normalizedObjB[keyA], maxDepth - 1, cache)) {
            return false;
        }
    }
    return true;
}
/**
 * Проверяет, являются ли два значения одинаковыми.
 * Учитывает особенности NaN
 *
 * @param x Первое значение.
 * @param y Второе значение.
 * @returns true, если значения равны, иначе false.
 */
function is(x, y) {
    if (x === y) {
        return x !== 0 || y !== 0 || 1 / x === 1 / y;
    }
    return x !== x && y !== y;
}
/**
 * Проверяет, является ли переданный объект итерируемым объектом (не массивом).
 *
 * @param obj Объект для проверки.
 * @returns true, если объект является итерируемым, иначе false.
 */
function isIterableObject(obj) {
    return obj !== null && !isArray(obj) && Symbol.iterator in obj;
}
/**
 * Преобразует итерируемый объект в обычный объект.
 *
 * @param obj Итерируемый объект.
 * @returns Обычный объект.
 */
function iterableToObject(obj) {
    const entries = [...obj];
    if (entries.length === 0) {
        return {};
    }
    // Предполагаем что это [key, value]
    const firstEntry = entries[0];
    if (isArray(firstEntry) && firstEntry.length === 2) {
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- для экономии не проверяем все значения
        return Object.fromEntries(entries);
    }
    // Тут массивы и другие неизвестные структуры
    return entries;
}
